/**
 * Game Engine - Main Entry Point
 *
 * Coordinates all engine systems:
 * - Game loop (fixed timestep physics)
 * - Renderer (Three.js)
 * - Physics (Cannon-ES)
 * - Audio (Howler)
 * - Input (Touch/Pointer)
 * - Events
 */

import { GameLoop, GameLoopConfig } from './core/GameLoop';
import { EventEmitter, AllEvents } from './core/EventEmitter';
import { Renderer, RendererConfig, RenderQuality } from './render/Renderer';
import { PhysicsWorld, PhysicsConfig } from './physics/PhysicsWorld';
import { AudioManager } from './audio/AudioManager';
import { InputManager, GestureConfig } from './input/InputManager';
import { renderScheduler, INPUT_PULSE_MS } from './render/RenderScheduler';
import { adaptiveResolution } from './render/AdaptiveResolution';

export interface EngineConfig {
  container: HTMLElement;
  renderer?: Partial<RendererConfig>;
  physics?: PhysicsConfig;
  loop?: GameLoopConfig;
  input?: GestureConfig;
  /** Reuse an AudioManager created before the engine (e.g. so the login screen
   *  can make sounds before the engine is built). Falls back to a fresh one. */
  audio?: AudioManager;
}

export class Engine extends EventEmitter<AllEvents> {
  // Core systems
  readonly renderer: Renderer;
  readonly physics: PhysicsWorld;
  readonly audio: AudioManager;
  readonly input: InputManager;

  // Game loop
  private gameLoop: GameLoop;
  private isRunning = false;

  // Update callbacks
  private fixedUpdateCallbacks: Set<(dt: number) => void> = new Set();
  private updateCallbacks: Set<(dt: number) => void> = new Set();
  private renderCallbacks: Set<(interpolation: number) => void> = new Set();

  constructor(config: EngineConfig) {
    super();

    // Initialize renderer
    this.renderer = new Renderer({
      container: config.container,
      ...config.renderer,
    });

    // Initialize physics
    this.physics = new PhysicsWorld(config.physics);

    // Initialize audio (reuse one created pre-engine if provided, so login-screen
    // sounds and the in-game catalog share a single AudioManager).
    this.audio = config.audio ?? new AudioManager();

    // Initialize input
    this.input = new InputManager(this.renderer.canvas, config.input);

    // Setup input event forwarding
    this.setupInputForwarding();

    // Initialize game loop
    this.gameLoop = new GameLoop(
      {
        fixedUpdate: this.fixedUpdate.bind(this),
        update: this.update.bind(this),
        render: this.render.bind(this),
      },
      config.loop
    );

    // While any die is airborne, lock the loop to one fixed step per frame so
    // the roll runs an identical step count on every client (cross-device dice
    // determinism). Derived from physics state each frame — no bookkeeping to
    // get stuck on. See GameLoop.setFixedStepLock + PhysicsWorld.anyDiceInFlight.
    this.gameLoop.setFixedStepLock(() => this.physics.anyDiceInFlight());
  }

  private setupInputForwarding(): void {
    // Any input touches the 3D world (pickup, drag, throw windup, betting) —
    // keep the renderer at full rate through the gesture. Renewed on every
    // event, so gaps between moves don't drop to the idle heartbeat mid-drag.
    const pulse = () => renderScheduler.pulse(INPUT_PULSE_MS);
    // Forward input events to engine event system
    this.input.on('input:touch_start', (data) => { pulse(); this.emit('input:touch_start', data); });
    this.input.on('input:touch_move', (data) => { pulse(); this.emit('input:touch_move', data); });
    this.input.on('input:touch_end', (data) => { pulse(); this.emit('input:touch_end', data); });
    this.input.on('input:pointer_down', (data) => { pulse(); this.emit('input:pointer_down', data); });
    this.input.on('input:pointer_move', (data) => { pulse(); this.emit('input:pointer_move', data); });
    this.input.on('input:pointer_up', (data) => { pulse(); this.emit('input:pointer_up', data); });
  }

  /**
   * Fixed timestep update (physics)
   * Stores previous state before stepping for interpolation
   */
  /** The fixed physics step (s). Pre-run simulation must use this exact dt. */
  get fixedDelta(): number {
    return this.gameLoop.fixedDelta;
  }

  private fixedUpdate(deltaTime: number): void {
    // Store previous state for interpolation before stepping
    this.physics.storePreviousState();

    // Step physics
    this.physics.step(deltaTime);

    // Call registered fixed update callbacks
    for (const callback of this.fixedUpdateCallbacks) {
      callback(deltaTime);
    }
  }

  /**
   * Variable update (game logic)
   */
  private update(deltaTime: number): void {
    // Call registered update callbacks
    for (const callback of this.updateCallbacks) {
      callback(deltaTime);
    }

    // Automatic activity source: while any dynamic body is awake (dice rolling
    // or settling — held mass-0 dice are excluded) keep the renderer at full
    // rate. The moment everything sleeps, this stops renewing and the render
    // gate falls to the idle heartbeat. Covers dice shoot / drop with no
    // per-site code.
    if (!this.physics.areAllBodiesSleeping()) {
      renderScheduler.pulse();
      // Awake dynamic bodies are shadow casters in motion (rolling dice,
      // dropped bills) — the shadow map must follow them.
      renderScheduler.markShadowsDirty();
    }
  }

  /**
   * Render
   */
  private render(interpolation: number): void {
    // On-demand render gate: at full rate while something is animating, else a
    // slow idle heartbeat. Skipping the GPU draw on a static board is the whole
    // thermal win. The game loop keeps ticking (physics + logic still run) — we
    // only skip the visual sync + GPU draw, which is the expensive part. The
    // heartbeat guarantees a missed activity source self-heals in ≤125ms rather
    // than freezing the frame.
    if (!renderScheduler.shouldRender()) return;

    // Interpolate physics bodies for smooth visuals
    this.physics.interpolate(interpolation);

    // Call registered render callbacks (sync visuals to physics)
    for (const callback of this.renderCallbacks) {
      callback(interpolation);
    }

    // Render the scene
    this.renderer.render();

    // Feed dynamic resolution. Only full-rate ACTIVE frames reflect real GPU
    // load — the render-on-demand idle heartbeat (8fps by design) must never be
    // read as jank, so it's gated on isActive().
    const now = performance.now();
    if (renderScheduler.isActive(now)) adaptiveResolution.sample(now);
  }

  /**
   * Start the engine
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.gameLoop.start();
    // Render generously through scene entry (camera intro, dice creation, asset
    // pop-in) so the first seconds are never throttled to the heartbeat.
    renderScheduler.pulse(2500);
    renderScheduler.markShadowsDirty();
    this.emit('engine:start', undefined);
  }

  /**
   * Stop the engine
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.gameLoop.stop();
    this.emit('engine:stop', undefined);
  }

  /**
   * Pause the engine (keeps rendering, stops updates)
   */
  pause(): void {
    // TODO: Implement pause state
    this.emit('engine:pause', undefined);
  }

  /**
   * Resume the engine
   */
  resume(): void {
    // TODO: Implement resume
    this.emit('engine:resume', undefined);
  }

  /**
   * Register a fixed update callback (physics rate)
   */
  onFixedUpdate(callback: (deltaTime: number) => void): () => void {
    this.fixedUpdateCallbacks.add(callback);
    return () => this.fixedUpdateCallbacks.delete(callback);
  }

  /**
   * Register a variable update callback (every frame)
   */
  onUpdate(callback: (deltaTime: number) => void): () => void {
    this.updateCallbacks.add(callback);
    return () => this.updateCallbacks.delete(callback);
  }

  /**
   * Register a render callback (every frame, after physics interpolation)
   */
  onRender(callback: (interpolation: number) => void): () => void {
    this.renderCallbacks.add(callback);
    return () => this.renderCallbacks.delete(callback);
  }

  /**
   * Get performance stats
   */
  getStats() {
    return {
      loop: this.gameLoop.getStats(),
      render: this.renderer.getStats(),
    };
  }

  /**
   * Set render quality
   */
  setQuality(quality: RenderQuality): void {
    this.renderer.setQuality(quality);
  }

  /**
   * Check if engine is running
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Get Three.js scene
   */
  get scene() {
    return this.renderer.scene;
  }

  /**
   * Get Three.js camera
   */
  get camera() {
    return this.renderer.camera;
  }

  /**
   * Get canvas element
   */
  get canvas() {
    return this.renderer.canvas;
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    this.stop();

    this.input.dispose();
    this.audio.dispose();
    this.physics.dispose();
    this.renderer.dispose();

    this.fixedUpdateCallbacks.clear();
    this.updateCallbacks.clear();
    this.renderCallbacks.clear();
    this.removeAllListeners();
  }
}

// Export all engine modules
export * from './core';
export * from './render/Renderer';
export * from './physics/PhysicsWorld';
export * from './audio/AudioManager';
export * from './input/InputManager';
